import React, { useState, useRef, useEffect } from 'react';

const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'All Customers',
  className = '',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Derive the label for the currently selected option
  const selectedLabel = options.find((o) => String(o.id) === String(value))?.name || '';

  // What to show inside the input box
  const displayValue = open ? search : selectedLabel;

  // Filtered list
  const filtered = options.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  // Open dropdown — blocked when disabled
  const handleFocus = () => {
    if (disabled) return;
    setSearch('');
    setOpen(true);
  };

  // Select an option
  const handleSelect = (option) => {
    onChange(String(option.id));
    setSearch('');
    setOpen(false);
  };

  // Clear selection — blocked when disabled
  const handleClear = (e) => {
    if (disabled) return;
    e.stopPropagation();
    onChange('');
    setSearch('');
    setOpen(false);
  };

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">

        {/* Search / magnifier icon */}
        <span className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none text-gray-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => {
            if (disabled) return;
            setSearch(e.target.value);
          }}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={!open || disabled}
          className={`w-full pl-8 pr-8 py-2 border border-gray-300 rounded-lg text-sm transition-all
            focus:ring-2 focus:ring-blue-500 focus:border-transparent
            ${disabled
              ? 'bg-gray-100 cursor-not-allowed opacity-60 text-gray-500'
              : 'bg-white cursor-pointer'
            }`}
        />

        {/* Clear (×) button — only when something is selected and not disabled */}
        {value && !open && !disabled && (
          <button
            onMouseDown={handleClear}
            className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
            tabIndex={-1}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Chevron — only when nothing selected and not disabled */}
        {!value && !disabled && (
          <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none text-gray-400">
            <svg
              className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        )}

      </div>

      {/* Dropdown */}
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">

          {/* Placeholder / clear option */}
          <div
            onMouseDown={() => { onChange(''); setSearch(''); setOpen(false); }}
            className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-700
              ${value === '' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-500 italic'}`}
          >
            {placeholder}
          </div>

          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400 italic">No customers found</div>
          ) : (
            filtered.map((option) => (
              <div
                key={option.id}
                onMouseDown={() => handleSelect(option)}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors
                  ${String(option.id) === String(value)
                    ? 'bg-indigo-50 text-indigo-700 font-semibold'
                    : 'text-gray-700'
                  }`}
              >
                {search ? highlightMatch(option.name, search) : option.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/** Wraps matched substring in a <mark> */
function highlightMatch(text, query) {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 text-gray-900 rounded">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default SearchableSelect;